"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchCustomerFeatures = exports.triggerContractFilesFetching = exports.handleContractFiles = exports.handleCompanyLogo = exports.saveStep1 = exports.getContractFiles = exports.getCustomer = exports.changeStep = void 0;
const fp = require("fp-ts");
const t = require("io-ts");
const monocle = require("monocle-ts");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const sok_types_1 = require("@caddigital/sok-types");
const ActionTypes = require("../../actionTypes/customer/wizard");
const Actions = require("../../actions/customer/wizard");
const validation_1 = require("../../utils/validation");
const errorHandling_1 = require("../../utils/errorHandling");
const features_1 = require("../../utils/features");
const wizardLens = monocle.Lens.fromPath([
    'customer',
    'wizard'
]);
const stepLens = (step) => new monocle.Optional((s) => {
    const key = 'step' + step;
    const values = s[key];
    if (values) {
        return fp.option.some(values);
    }
    return fp.option.none;
}, (_a) => (s) => s);
const activeStepLens = monocle.Lens.fromPath(['customer', 'wizard', 'wizardStep']);
const validateStep = (step) => (store) => {
    const currentStep = activeStepLens.get(store);
    if (currentStep > step) {
        return fp.validation.success(true);
    }
    if (currentStep === 0) {
        return wizardLens
            .asOptional()
            .compose(stepLens(1))
            .getOption(store)
            .fold(fp.validation.failure([
            {
                key: 'any',
                value: 'Unhandled exception'
            }
        ]), (x) => {
            const val = fp.function.compose((0, validation_1.ap)((0, features_1.validateFeatures)(x.features)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('companyName')(x)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('street')(x)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('postalCode')(x)), (0, validation_1.ap)((0, validation_1.isNotEmpty)('city')(x)))((0, validation_1.isNotEmpty)('country')(x));
            if (x.hasBusinessId) {
                return (0, validation_1.ap)((0, validation_1.isNotEmpty)('businessId')(x))(val);
            }
            else {
                return val;
            }
        });
    }
    return fp.validation.success(true);
};
function changeStep(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.CHANGE_STEP), (0, operators_1.mergeMap)((request) => validateStep(request.payload.wizardStep)(store.value).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), (_) => (0, rxjs_1.of)(Actions.validateStepSuccess(request.payload.wizardStep)))));
}
exports.changeStep = changeStep;
function getCustomer(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_CUSTOMER_REQUEST), (0, operators_1.switchMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/customer/${request.payload}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        const validation = sok_types_1.Customer.iExtendedCustomer.decode(response);
        const act = validation.fold((err) => Actions.fetchCustomerFailure((0, errorHandling_1.parseValidationError)(err)), (e) => Actions.fetchCustomerSuccess(e));
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((err) => (0, rxjs_1.of)(Actions.fetchCustomerFailure((0, errorHandling_1.parseAjaxErrorResponse)(err)))))));
}
exports.getCustomer = getCustomer;
function getContractFiles(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_CONTRACT_FILES_REQUEST), (0, operators_1.switchMap)((request) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/customer/${request.payload}/contract-file`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        const validation = t.array(sok_types_1.Customer.iContractFile).decode(response);
        const act = validation.fold((err) => Actions.fetchContractFilesFailure((0, errorHandling_1.parseValidationError)(err)), (e) => Actions.fetchContractFilesSuccess(e));
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((err) => (0, rxjs_1.of)(Actions.fetchContractFilesFailure((0, errorHandling_1.parseAjaxErrorResponse)(err)))))));
}
exports.getContractFiles = getContractFiles;
// Handle step 1 saving
// validate data
// Check if id
// dispatch all save actions
// if new customer, dispatch actions after we have id
function saveStep1(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SAVE_CUSTOMER_REQUEST), (0, operators_1.mergeMap)((request) => {
        const s = store.value;
        return validateStep(0)(s).fold((e) => (0, rxjs_1.of)(Actions.validateStepFailure(e)), () => ajax({
            headers: {
                'Content-Type': 'application/json',
                Authorization: getToken()
            },
            method: request.payload.uuid === null ? 'POST' : 'PUT',
            body: request.payload.uuid === null
                ? request.payload.basicInformation
                : Object.assign({ uuid: request.payload.uuid }, request.payload.basicInformation),
            url: `${baseUrl}api/customer/`
        }).pipe((0, operators_1.mergeMap)(({ response }) => sok_types_1.Customer.iExtendedCustomer
            .decode(response)
            .fold((err) => (0, rxjs_1.of)(Actions.saveCustomerFailure((0, errorHandling_1.parseValidationError)(err))), (e) => (0, rxjs_1.merge)((0, rxjs_1.of)(Actions.saveCustomerSuccess(e)), request.payload.customerLogo
            ? (0, rxjs_1.of)(Actions.setCustomerLogoRequest(e.uuid, request.payload.customerLogo))
            : (0, rxjs_1.empty)(), request.payload.contractFiles.length > 0
            ? (0, rxjs_1.of)(Actions.uploadContractFileRequest(e.uuid, request.payload.contractFiles))
            : (0, rxjs_1.empty)()))), (0, operators_1.catchError)((err) => (0, rxjs_1.of)(Actions.saveCustomerFailure((0, errorHandling_1.parseAjaxErrorResponse)(err))))));
    }));
}
exports.saveStep1 = saveStep1;
const getFileForm = (file) => {
    const form = new FormData();
    file && form.append('file', file, file.name);
    return form;
};
function handleCompanyLogo(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.SET_CUSTOMER_LOGO_REQUEST), (0, operators_1.mergeMap)((request) => ajax({
        headers: {
            Authorization: getToken()
        },
        method: 'POST',
        body: getFileForm(request.payload.file),
        url: `${baseUrl}api/customer/${request.payload.uuid}/customer-image`
    }).pipe((0, operators_1.mergeMap)(({ response }) => {
        const act = t
            .interface({ message: t.string })
            .decode(response)
            .fold((err) => Actions.setCustomerLogoFailure((0, errorHandling_1.parseValidationError)(err)), (e) => Actions.setCustomerLogoSuccess());
        return (0, rxjs_1.of)(act);
    }), (0, operators_1.catchError)((err) => (0, rxjs_1.of)(Actions.setCustomerLogoFailure((0, errorHandling_1.parseAjaxErrorResponse)(err)))))));
}
exports.handleCompanyLogo = handleCompanyLogo;
function handleContractFiles(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.UPLOAD_CONTRACT_FILE_REQUEST), (0, operators_1.mergeMap)(({ payload }) => {
        // convert list of files to single objects
        // this help as to track single file easily
        // and we can now use rx queue
        // currently 10 files same time
        return (0, rxjs_1.of)(...payload.files.map((file) => ({
            uuid: payload.uuid,
            file
        })));
    }), (0, operators_1.mergeMap)((request) => {
        const replay = new rxjs_1.ReplaySubject();
        const upload = ajax({
            headers: {
                Authorization: getToken()
            },
            method: 'POST',
            body: getFileForm(request.file.file),
            url: `${baseUrl}api/customer/${request.uuid}/contract-file`,
            progressSubscriber: new rxjs_1.Subscriber(replay)
        }).pipe((0, operators_1.mergeMap)(({ response }) => {
            const act = sok_types_1.Customer.iContractFile
                .decode(response)
                .fold((err) => Actions.uploadContractFileFailure(request.file.uuid, (0, errorHandling_1.parseValidationError)(err)), (e) => Actions.uploadContractFileSuccess(request.file.uuid));
            return (0, rxjs_1.of)(act);
        }), (0, operators_1.catchError)((err) => (0, rxjs_1.of)(Actions.uploadContractFileFailure(request.file.uuid, (0, errorHandling_1.parseAjaxErrorResponse)(err)))));
        return (replay.pipe((0, operators_1.mergeMap)((e) => {
            // Handle fileupload status
            if (e.type && e.type === 'progress') {
                return (0, rxjs_1.of)(Actions.uploadContractFileStatus(request.file.uuid, Math.floor((e.loaded / e.total) * 100)));
            }
            else {
                return (0, rxjs_1.empty)();
            }
        }), (0, operators_1.catchError)((err) => {
            if (err.type === 'readystatechange') {
                return (0, rxjs_1.empty)();
            }
            return (0, rxjs_1.of)(Actions.uploadContractFileFailure(request.file.uuid, (0, errorHandling_1.parseAjaxErrorResponse)(err)));
        })),
            (0, rxjs_1.merge)(upload));
    }, undefined, 5));
}
exports.handleContractFiles = handleContractFiles;
function triggerContractFilesFetching(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.UPLOAD_CONTRACT_FILE_SUCCESS), (0, operators_1.mergeMap)((request) => {
        const customerId = store.value.customer.wizard.customerId;
        const uploadedFiles = store.value.customer.wizard.uploadedItems;
        const files = Object.values(uploadedFiles);
        const done = files.filter(({ status }) => status === 'UPLOADED');
        if (files.length === done.length && customerId !== null) {
            return (0, rxjs_1.of)(Actions.fetchContractFilesRequest(customerId));
        }
        return (0, rxjs_1.empty)();
    }));
}
exports.triggerContractFilesFetching = triggerContractFilesFetching;
function fetchCustomerFeatures(action$, store, { ajax, getToken, baseUrl }) {
    return action$.pipe((0, operators_1.filter)((action) => action.type === ActionTypes.FETCH_FEATURES_REQUEST), (0, operators_1.mergeMap)((action) => ajax({
        headers: {
            'Content-Type': 'application/json',
            Authorization: getToken()
        },
        method: 'GET',
        url: `${baseUrl}api/admin/features/customers/${action.payload}`
    }).pipe((0, operators_1.mergeMap)(({ response }) => t
        .array(sok_types_1.Feature.iFeature)
        .decode(response)
        .fold((error) => (0, rxjs_1.of)(Actions.fetchFeaturesFailed((0, errorHandling_1.parseValidationError)(error))), (res) => (0, rxjs_1.of)(Actions.fetchFeaturesSuccess(res)))), (0, operators_1.catchError)((error) => (0, rxjs_1.of)(Actions.fetchFeaturesFailed((0, errorHandling_1.parseAjaxErrorResponse)(error)))))));
}
exports.fetchCustomerFeatures = fetchCustomerFeatures;
//# sourceMappingURL=wizard.js.map